/**
 * KullaniciKartiPage.jsx — Kullanıcı Kartı (backlog #379). Rota: /Users/:userId (page.kullanici).
 *
 * Tek okuma: `GET /users/{id}/kart` (userService.getKullaniciKarti, backend #378) — kullanıcı satırı + atandığı fuarlar + firmasının
 * çalıştığı fuarlar + kişi bağlantıları. Künye başlığı (KullaniciBasligi) ve üç sekme: Fuarlar · Kişi · Rol; `?tab=fuarlar|kisi|rol`
 * adreste durur. Sekmelerin hepsi aynı okumadan beslenir, bu yüzden hepsi monte kalır.
 *
 * Yazmalar (kişi bağla / kaldır, rol değiştir, düzenle, pasifleştir / aktifleştir) bittiğinde `reload()` kartı YENİDEN okur; içerik
 * silinmez, tam sayfa dönen simge yalnızca kullanıcı değişince çıkar (açık pencereler ve sekme durumu yerinde kalsın).
 *
 * #545 (2026-10-26): başlıktaki Düzenle artık tüm alanları (kullanıcı adı, yeni şifre, ad, soyad, ünvan, telefon, e-posta, uyruk, durum)
 * düzenler (UserFormModal) ve silmenin yerine Pasifleştir / Aktifleştir geldi (`userActive` 0 / 1). Düğmeler yalnızca MANAGE_USER_ROLES
 * sahibine çizilir (kök de geçer). Kayıt telefonu / e-postayı başka kullanıcıların taşıdığı bir değere taşıdıysa (#490) backend yine
 * kaydeder ve `uyarilar[]` döner: sayfa bunları kaydın altında, diğer kullanıcılara bağlantılarıyla gösterir (kendiliğinden kalkmaz).
 * Yeni kullanıcı oluşturulunca liste bu karta `state.info` ile gelir.
 *
 * #556 (2026-10-28): başlıktaki "Şifre bağlantısı oluştur" `POST /users/{id}/sifre-linki` (backend #555) çağırır ve sonucu SifreLinkiModal'da
 * gösterir (tam adres + Kopyala); anahtar yalnızca bir kez gelir, bu yüzden state'ten başka yerde tutulmaz. Herkese açık karşılığı
 * /sifre-belirle?anahtar= sayfası (pages/Home/SifreBelirle).
 *
 * #572 (2026-10-29): başlıktaki "Aktivasyon maili gönder" `POST /users/{id}/aktivasyon-maili` (backend #568) çağırır: kullanıcıya KENDİ e-postasına yeni bir
 * şifre belirleme bağlantısı gider. Önce onay penceresi (yeni bağlantı öncekini iptal eder); sonuç kartın üstünde kalır (SENT / BLOCKED güvenli mod / FAILED +
 * nedeni). Bağlantı yanıtta yoktur, sayfa hiçbir yerde göstermez.
 *
 * #611 (2026-11-03): başlıktaki "MFA sıfırla" `POST /mfa/reset/{userId}` (backend #639, MANAGE_USER_ROLES) çağırır: telefonunu kaybeden kullanıcının
 * ikinci faktörü kapanır (önce onay penceresi); Düzenle'de artık kullanıcının dili de vardır (UserFormModal).
 *
 * "Geri" bağlantısı Kişi Detay'ınkiyle aynı düzen (#262 / #273): önce router state'i (listeden aynı sekmede gelindi, kaydırma konumuyla), yoksa
 * `?geri=` / `?geriEtiket=` (yeni sekmede açılan bağlantılar), ikisi de yoksa Kullanıcılar listesi.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  Alert, Button, Card, Container, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, Spinner, TabContent, TabPane,
} from "reactstrap";
import { ArrowLeft, CalendarDays, Link2, UserCog } from "lucide-react";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { geriHedefiOku } from "../../../Components/Kisi/kisiBaglantilari";
import { adSoyad, aktifMi } from "../../../Components/Kullanici/KullaniciParcalari";
import { createSifreLinki, getKullaniciKarti, sendAktivasyonMaili, updateUser } from "../../../services/userService";
import { PERMISSION_KEYS, getMyPermissions } from "../../../services/rbacService";
import { kullaniciMfaSifirla } from "../../../services/mfaService";
import i18n from "../../../i18n";
import KullaniciBasligi from "./Kart/KullaniciBasligi";
import FuarlarSekmesi from "./Kart/FuarlarSekmesi";
import KisiSekmesi from "./Kart/KisiSekmesi";
import RolSekmesi from "./Kart/RolSekmesi";
import SifreLinkiModal from "./Kart/SifreLinkiModal";
import UserFormModal from "./UserFormModal";

const TABS = [
  { key: "fuarlar", labelKey: "kullaniciKartiPage.fuarlar", Icon: CalendarDays },
  { key: "kisi", labelKey: "kullaniciKartiPage.kisi", Icon: Link2 },
  { key: "rol", labelKey: "kullaniciKartiPage.rol", Icon: UserCog },
];

export default function KullaniciKartiPage() {
  const { t } = useTranslation("yonetim");
  const { userId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [kart, setKart] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(() => (typeof location.state?.info === "string" ? location.state.info : null));
  const [uyarilar, setUyarilar] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);
  const [duzenleAcik, setDuzenleAcik] = useState(false);
  const [durumAcik, setDurumAcik] = useState(false);
  const [durumIsleniyor, setDurumIsleniyor] = useState(false);
  const [ben, setBen] = useState(null);
  // #556: the answer of POST /users/{id}/sifre-linki. The key comes back only once, so it lives here and nowhere else (not stored, not logged).
  const [sifreLinki, setSifreLinki] = useState(null);
  const [sifreLinkiIsleniyor, setSifreLinkiIsleniyor] = useState(false);
  // #572: aktivasyon maili — onay penceresi, istek sürüyor, sonuç (SENT | BLOCKED | FAILED; bağlantı hiçbir zaman gelmez)
  const [postaAcik, setPostaAcik] = useState(false);
  const [postaIsleniyor, setPostaIsleniyor] = useState(false);
  const [postaSonucu, setPostaSonucu] = useState(null);
  // #611: MFA sıfırla — onay penceresi, istek sürüyor
  const [mfaAcik, setMfaAcik] = useState(false);
  const [mfaIsleniyor, setMfaIsleniyor] = useState(false);

  const id = Number(userId);
  const activeTab = TABS.some((x) => x.key === searchParams.get("tab")) ? searchParams.get("tab") : "fuarlar";
  // Başka kullanıcının kartı ekranda kalmasın: yalnızca şu anki kullanıcıya ait okuma gösterilir.
  const gecerli = kart && kart.kullanici?.userId === id ? kart : null;
  // Yazma düğmeleri MANAGE_USER_ROLES ister (kök her izne sahiptir); bilinmiyorsa (okuma sürüyor ya da düştü) hiç çizilmez.
  const yazabilir = Boolean(ben?.root) || (ben?.permissions || []).includes(PERMISSION_KEYS.MANAGE_USER_ROLES);
  const kendisi = !ben?.root && ben?.userId === id;

  useEffect(() => { document.title = i18n.t("yonetim:kullaniciKartiPage.sayfaBasligi"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const me = await getMyPermissions();
        if (!ignore) setBen(me);
      } catch {
        if (!ignore) setBen(null);
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setError(null);
      try {
        const d = await getKullaniciKarti(id);
        if (!ignore) setKart(d);
      } catch (e) {
        if (!ignore) setError(e.status === 404 ? i18n.t("yonetim:kullaniciKartiPage.kullaniciBulunamadi") : (e.message || i18n.t("yonetim:kullaniciKartiPage.kartOkunamadi")));
      }
    }
    run();
    return () => { ignore = true; };
  }, [id, reloadToken]);

  // Başarı iletisi birkaç saniye sonra kendiliğinden kalkar.
  useEffect(() => {
    if (!info) return undefined;
    const h = setTimeout(() => setInfo(null), 5000);
    return () => clearTimeout(h);
  }, [info]);

  const reload = () => setReloadToken((n) => n + 1);

  function setTab(key) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    // state'i taşı: listenin verdiği "geri dön" adresi sekme değişince silinmesin.
    setSearchParams(next, { replace: true, state: location.state });
  }

  function changed(mesaj) {
    if (mesaj) setInfo(mesaj);
    reload();
  }

  async function durumuDegistir() {
    const hedefAktif = !aktifMi(gecerli.kullanici);
    setDurumIsleniyor(true);
    try {
      await updateUser(id, { userActive: hedefAktif ? 1 : 0 });
      setDurumAcik(false);
      changed(t(hedefAktif ? "kullaniciKartiPage.aktiflestirildi" : "kullaniciKartiPage.pasiflestirildi", { ad: adSoyad(gecerli.kullanici) }));
    } catch (e) {
      setDurumAcik(false);
      setError(e.message || t("kullaniciKartiPage.durumDegistirilemedi"));
    } finally {
      setDurumIsleniyor(false);
    }
  }

  async function sifreLinkiOlustur() {
    setSifreLinkiIsleniyor(true);
    setError(null);
    try {
      setSifreLinki(await createSifreLinki(id));
    } catch (e) {
      // 403 (kökün hesabı başkasında) dahil: backend'in kendi metni, isteğin dilinde.
      setError(e.message || t("kullaniciKartiPage.sifreLinkiOlusturulamadi"));
    } finally {
      setSifreLinkiIsleniyor(false);
    }
  }

  async function aktivasyonMailiGonder() {
    setPostaIsleniyor(true);
    setError(null);
    try {
      const sonuc = await sendAktivasyonMaili(id);
      setPostaAcik(false);
      setPostaSonucu(sonuc);
      if (sonuc?.sent) reload();   // başlıktaki "aktivasyon maili" tarihi
    } catch (e) {
      // 400 (geçerli adres yok), 403 (kökün hesabı başkasında), 404: backend'in kendi metni, isteğin dilinde.
      setPostaAcik(false);
      setError(e.message || t("kullaniciKartiPage.aktivasyonMailiGonderilemedi"));
    } finally {
      setPostaIsleniyor(false);
    }
  }

  async function mfaSifirla() {
    setMfaIsleniyor(true);
    setError(null);
    try {
      const sonuc = await kullaniciMfaSifirla(id);
      setMfaAcik(false);
      // 403 (kökün hesabı başkasında), 404: backend'in kendi metni, isteğin dilinde.
      if (sonuc.ok) setInfo(t("kullaniciKartiPage.mfaSifirlandi", { ad: adSoyad(gecerli.kullanici) }));
      else setError(sonuc.message || t("kullaniciKartiPage.mfaSifirlanamadi"));
    } catch (e) {
      setMfaAcik(false);
      setError(e.message || t("kullaniciKartiPage.mfaSifirlanamadi"));
    } finally {
      setMfaIsleniyor(false);
    }
  }

  const geriState = typeof location.state?.from === "string" ? location.state : null;
  const geriParam = geriHedefiOku(searchParams);
  const backHref = geriState?.from ?? geriParam?.from ?? "/Users";
  const backLabel = geriState?.fromLabel ?? geriParam?.fromLabel ?? t("kullaniciKartiPage.kullanicilaraDon");
  const buKart = `${location.pathname}${location.search}`;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("kullaniciKartiPage.kullaniciKarti")} pageTitle={t("kullaniciKartiPage.kullanicilar")} />

        <Link
          to={backHref} state={geriState?.scrollY != null ? { scrollY: geriState.scrollY } : undefined}
          className="d-inline-flex align-items-center gap-1 mb-3 text-decoration-none"
        >
          <ArrowLeft size={14} />{backLabel}
        </Link>

        {error && <Alert fade={false} color="danger" toggle={() => setError(null)}>{error}</Alert>}
        {info && <Alert fade={false} color="success" toggle={() => setInfo(null)}>{info}</Alert>}
        {postaSonucu && (
          <Alert fade={false} color={postaSonucu.sent ? "success" : postaSonucu.status === "BLOCKED" ? "warning" : "danger"} toggle={() => setPostaSonucu(null)}>
            <div>{postaSonucu.message}</div>
            {postaSonucu.error && <div className="small">{postaSonucu.error}</div>}
            {postaSonucu.sent && postaSonucu.inactive && <div className="small mt-1">{t("sifreLinkiModal.pasifUyarisi")}</div>}
          </Alert>
        )}
        {uyarilar.length > 0 && (
          <Alert fade={false} color="warning" toggle={() => setUyarilar([])}>
            {uyarilar.map((u, i) => (
              <div key={u.kod ? `${u.kod}-${i}` : i} className={i > 0 ? "mt-2" : undefined}>
                <div>{u.mesaj}</div>
                {(u.kullanicilar || []).length > 0 && (
                  <div className="small">
                    {u.kullanicilar.map((k, j) => (
                      <span key={k.userId}>
                        {j > 0 && ", "}
                        <Link
                          to={`/Users/${k.userId}?geri=${encodeURIComponent(buKart)}&geriEtiket=${encodeURIComponent(t("kullaniciKartiPage.kullaniciKartinaDon"))}`}
                          className="fw-semibold"
                        >
                          {k.adSoyad || k.kullaniciAdi || t("kullaniciKartiPage.kullaniciNo", { id: k.userId })}
                        </Link>
                        {k.aktif === false && <span className="text-muted"> ({t("kullaniciKartiPage.pasif")})</span>}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Alert>
        )}

        {!gecerli && !error && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{t("common:yukleniyor")}</p>
          </div>
        )}

        {gecerli && (
          <>
            <KullaniciBasligi
              kullanici={gecerli.kullanici} yazabilir={yazabilir} kendisi={kendisi}
              onDuzenle={() => setDuzenleAcik(true)} onDurum={() => setDurumAcik(true)}
              onSifreLinki={sifreLinkiOlustur} sifreLinkiIsleniyor={sifreLinkiIsleniyor}
              onAktivasyonMaili={() => setPostaAcik(true)} aktivasyonMailiIsleniyor={postaIsleniyor}
              onMfaSifirla={() => setMfaAcik(true)} mfaSifirlaIsleniyor={mfaIsleniyor}
            />

            {sifreLinki && <SifreLinkiModal sifreLinki={sifreLinki} onClose={() => setSifreLinki(null)} />}

            <Card className="mb-3">
              <Nav tabs className="nav-tabs-custom border-bottom-0 px-3">
                {TABS.map((x) => (
                  <NavItem key={x.key}>
                    <NavLink
                      href="#"
                      className={`fw-semibold d-inline-flex align-items-center gap-1 ${activeTab === x.key ? "active" : ""}`}
                      style={{ cursor: "pointer" }}
                      onClick={(e) => { e.preventDefault(); setTab(x.key); }}
                    >
                      <x.Icon size={14} />{t(x.labelKey)}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </Card>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="fuarlar"><FuarlarSekmesi kart={gecerli} /></TabPane>
              <TabPane tabId="kisi">
                <KisiSekmesi kart={gecerli} onChanged={changed} onError={(m) => setError(m)} />
              </TabPane>
              <TabPane tabId="rol"><RolSekmesi kart={gecerli} onChanged={changed} /></TabPane>
            </TabContent>

            {duzenleAcik && (
              <UserFormModal
                mode="edit" user={gecerli.kullanici}
                onClose={(sonuc) => {
                  setDuzenleAcik(false);
                  if (!sonuc) return;
                  setUyarilar(sonuc.uyarilar || []);
                  changed(t("kullaniciKartiPage.guncellendi", { ad: adSoyad(gecerli.kullanici) }));
                }}
              />
            )}

            <Modal isOpen={postaAcik} toggle={() => !postaIsleniyor && setPostaAcik(false)}>
              <ModalHeader toggle={() => !postaIsleniyor && setPostaAcik(false)}>{t("kullaniciKartiPage.aktivasyonMailiBaslik")}</ModalHeader>
              <ModalBody className="small">
                {t("kullaniciKartiPage.aktivasyonMailiAciklama", { ad: adSoyad(gecerli.kullanici), eposta: gecerli.kullanici.userEmail })}
              </ModalBody>
              <ModalFooter>
                <Button color="light" onClick={() => setPostaAcik(false)} disabled={postaIsleniyor}>{t("common:iptal")}</Button>
                <Button color="primary" onClick={aktivasyonMailiGonder} disabled={postaIsleniyor}>
                  {postaIsleniyor ? <Spinner size="sm" /> : t("kullaniciKartiPage.aktivasyonMailiGonder")}
                </Button>
              </ModalFooter>
            </Modal>

            <Modal isOpen={mfaAcik} toggle={() => !mfaIsleniyor && setMfaAcik(false)}>
              <ModalHeader toggle={() => !mfaIsleniyor && setMfaAcik(false)}>{t("kullaniciKartiPage.mfaSifirlansinMi")}</ModalHeader>
              <ModalBody className="small">{t("kullaniciKartiPage.mfaSifirlamaAciklamasi", { ad: adSoyad(gecerli.kullanici) })}</ModalBody>
              <ModalFooter>
                <Button color="light" onClick={() => setMfaAcik(false)} disabled={mfaIsleniyor}>{t("common:iptal")}</Button>
                <Button color="danger" onClick={mfaSifirla} disabled={mfaIsleniyor}>
                  {mfaIsleniyor ? <Spinner size="sm" /> : t("kullaniciBasligi.mfaSifirla")}
                </Button>
              </ModalFooter>
            </Modal>

            <Modal isOpen={durumAcik} toggle={() => !durumIsleniyor && setDurumAcik(false)}>
              <ModalHeader toggle={() => !durumIsleniyor && setDurumAcik(false)}>
                {aktifMi(gecerli.kullanici) ? t("kullaniciKartiPage.pasiflestirilsinMi") : t("kullaniciKartiPage.aktiflestirilsinMi")}
              </ModalHeader>
              <ModalBody className="small">
                {aktifMi(gecerli.kullanici)
                  ? t("kullaniciKartiPage.pasiflestirmeAciklamasi", { ad: adSoyad(gecerli.kullanici) })
                  : t("kullaniciKartiPage.aktiflestirmeAciklamasi", { ad: adSoyad(gecerli.kullanici) })}
              </ModalBody>
              <ModalFooter>
                <Button color="light" onClick={() => setDurumAcik(false)} disabled={durumIsleniyor}>{t("common:iptal")}</Button>
                <Button color={aktifMi(gecerli.kullanici) ? "danger" : "primary"} onClick={durumuDegistir} disabled={durumIsleniyor}>
                  {durumIsleniyor
                    ? <Spinner size="sm" />
                    : (aktifMi(gecerli.kullanici) ? t("kullaniciBasligi.pasiflestir") : t("kullaniciBasligi.aktiflestir"))}
                </Button>
              </ModalFooter>
            </Modal>
          </>
        )}
      </Container>
    </div>
  );
}
